import { useEffect, useState } from "react";

/**
 * 185: subscribe to a CSS media query. Used by the list-table reflow to render
 * EITHER the desktop `<table>` OR the mobile cards -- never both. A both-in-DOM
 * CSS toggle (`hidden sm:block` / `sm:hidden`) would put each row's `data-item`
 * attrs on two elements, doubling every selector count and turning `getItem(id)`
 * into a strict-mode violation when clicked. Rendering one tree keeps the
 * existing E2E selectors exact.
 *
 * Guarded for non-browser/jsdom environments (matchMedia may be absent) so a
 * component using it never crashes a unit test.
 */
export function useMediaQuery(query: string): boolean {
  const supported = typeof window !== "undefined" && typeof window.matchMedia === "function";
  const [matches, setMatches] = useState(() => (supported ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (!supported) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query, supported]);

  return matches;
}

/** True at Tailwind's `sm` breakpoint (640px) and up. */
export function useIsSmUp(): boolean {
  return useMediaQuery("(min-width: 640px)");
}
